import { defineMessages } from 'react-intl';

export const pricingPoliciesMessages = defineMessages({
    perUnitRequiresMetric: {
        id: 'per-unit-requires-metric',
        defaultMessage: 'Per-unit pricing requires the plan item to have a billable metric. Set one on the item first.',
    },
    pricingPolicies: {
        id: 'pricing-policies',
        defaultMessage: 'Pricing policies',
    },
    pricingPolicy: {
        id: 'pricing-policy',
        defaultMessage: 'Pricing policy',
    },
    freeQuantities: {
        id: 'free-quantities',
        defaultMessage: 'Free quantities',
    },
    policies: {
        id: 'policies',
        defaultMessage: 'Policies',
    },
    policy: {
        id: 'policy',
        defaultMessage: 'Policy',
    },
    amount: {
        id: 'amount',
        defaultMessage: 'Amount',
    },
    pre: {
        id: 'payment-type-pre',
        defaultMessage: 'Pre (next period)',
    },
    current: {
        id: 'payment-type-current',
        defaultMessage: 'Current period',
    },
    post: {
        id: 'payment-type-post',
        defaultMessage: 'Post (previous period)',
    },
    paymentTypeHint: {
        id: 'payment-type-hint',
        defaultMessage:
            'Which period a bill issued on 01.10 covers: post - September (the previous period), current - October, pre - November (the next period).',
    },
    paymentType: {
        id: 'payment-type',
        defaultMessage: 'Payment type',
    },
    pricingModel: {
        id: 'pricing-model',
        defaultMessage: 'Pricing model',
    },
    pricingModelFlat: {
        id: 'pricing-model-flat',
        defaultMessage: 'Flat',
    },
    pricingModelPerUnit: {
        id: 'pricing-model-per-unit',
        defaultMessage: 'Per unit',
    },
    taxReason: {
        id: 'tax-reason',
        defaultMessage: 'Tax reason',
    },
    pricingMode: {
        id: 'pricing-mode',
        defaultMessage: 'Price type',
    },
    pricingModeFixed: {
        id: 'pricing-mode-fixed',
        defaultMessage: 'Fixed price',
    },
    pricingModeIndexed: {
        id: 'pricing-mode-indexed',
        defaultMessage: 'Indexed to a reference rate',
    },
    referenceRate: {
        id: 'pricing-policy-reference-rate',
        defaultMessage: 'Reference rate',
    },
    ratePercent: {
        id: 'pricing-policy-rate-percent',
        defaultMessage: 'Percentage of the rate',
    },
    ratePercentHint: {
        id: 'pricing-policy-rate-percent-hint',
        defaultMessage: 'Enter a percentage - 50 means half of the reference rate.',
    },
    nonCurrencyReferenceRateHint: {
        id: 'pricing-policy-non-currency-reference-rate-hint',
        defaultMessage:
            "This rate is not in a currency: its value is used as a plain number (a percent as a fraction, 3.5 % is 0.035) and the result is an amount in the plan's currency.",
    },
    noReferenceRatesForCurrency: {
        id: 'pricing-policy-no-reference-rates-for-currency',
        defaultMessage: 'No active reference rate matches this plan\u2019s currency.',
    },
    referenceRateHasNoValueWarning: {
        id: 'pricing-policy-reference-rate-has-no-value-warning',
        defaultMessage:
            'This rate has no value covering today. Add a value to it before indexing a policy to it, or the policy will be rejected.',
    },
    createServicePlanPricingPolicySuccess: {
        id: 'create-service-plan-pricing-policy-success',
        defaultMessage: 'Pricing policy created',
    },
    createServicePlanPricingPolicySuccessDescription: {
        id: 'create-service-plan-pricing-policy-success-description',
        defaultMessage: 'A service plan pricing policy has been successfully created.',
    },
    addServicePlanPricingPolicySubtitle: {
        id: 'add-service-plan-pricing-policy-subtitle',
        defaultMessage: 'Create a new service plan pricing policy',
    },
    deleteServicePlanPricingPolicy: {
        id: 'delete-service-plan-pricing-policy',
        defaultMessage: 'Delete pricing policy',
    },
    deleteServicePlanPricingPolicySuccess: {
        id: 'delete-service-plan-pricing-policy-success',
        defaultMessage: 'Pricing policy deleted',
    },
    deleteServicePlanPricingPolicySuccessDescription: {
        id: 'delete-service-plan-pricing-policy-success-description',
        defaultMessage: 'The pricing policy has been successfully deleted.',
    },
    confirmDeleteServicePlanPricingPolicy: {
        id: 'confirm-delete-service-plan-pricing-policy',
        defaultMessage:
            'Are you sure you want to delete this service plan pricing policy? This action cannot be undone.',
    },
    deleteServicePlanPricingPolicySubtitle: {
        id: 'delete-service-plan-pricing-policy-subtitle',
        defaultMessage: 'Permanently delete service plan pricing policy',
    },
    editServicePlanPricingPolicySubtitle: {
        id: 'edit-service-plan-pricing-policy-subtitle',
        defaultMessage: 'Edit pricing policy',
    },
    updateServicePlanPricingPolicySuccess: {
        id: 'update-service-plan-pricing-policy-success',
        defaultMessage: 'Pricing policy updated',
    },
    updateServicePlanPricingPolicySuccessDescription: {
        id: 'update-service-plan-pricing-policy-success-description',
        defaultMessage: 'Service plan pricing policy has been successfully updated.',
    },
    editCurrentOrPastPricingPolicyError: {
        id: 'custom-error:edit_current_or_past_service_plan_item_pricing_policy',
        defaultMessage:
            "Only a pricing policy that has not started yet can be edited. This one's validity period has already begun, so add a new policy with a future start date instead!",
    },
    lastScheduledPricingPolicyError: {
        id: 'custom-error:delete_not_last_service_plan_item_pricing_policy',
        defaultMessage: 'Only the last scheduled service plan item pricing policy can be deleted!',
    },
    amountAndReferenceRateConflictError: {
        id: 'custom-error:amount_and_reference_rate_conflict',
        defaultMessage: 'A pricing policy can have either a fixed amount or a reference rate, not both!',
    },
    missingAmountAndReferenceRateError: {
        id: 'custom-error:missing_amount_and_reference_rate',
        defaultMessage: 'A pricing policy needs either a fixed amount or a reference rate!',
    },
    invalidRateMultiplierError: {
        id: 'custom-error:invalid_rate_multiplier',
        defaultMessage: 'The percentage of the reference rate must be greater than 0!',
    },
    foreignTenantReferenceRateError: {
        id: 'custom-error:foreign_tenant_reference_rate',
        defaultMessage: 'The selected reference rate belongs to another tenant!',
    },
    referenceRateCurrencyMismatchError: {
        id: 'custom-error:reference_rate_currency_mismatch',
        defaultMessage: "The reference rate's currency must match the service plan's currency!",
    },
    missingReferenceRateValueError: {
        id: 'custom-error:missing_reference_rate_value',
        defaultMessage:
            "The reference rate has no value covering the policy's start date. Add a value to the rate first!",
    },
    perUnitPricingWithoutMetricError: {
        id: 'custom-error:per_unit_pricing_without_metric',
        defaultMessage: 'Per-unit pricing requires the plan item to have a billable metric.',
    },
    deletePricingPolicyWithSubscriptionItemsError: {
        id: 'custom-error:delete_service_plan_item_pricing_policy_with_subscription_items',
        defaultMessage: 'Cannot delete a pricing policy whose service plan item is used by a subscription!',
    },
});
